import React, { useState } from 'react';
import { AlertCircle, Phone, MessageSquare, Shield, X } from 'lucide-react';
import { Button } from './ui';

/**
 * SOS Button Component
 * 
 * Universal crisis button available on all authenticated pages.
 * Provides immediate access to crisis resources:
 * - 988 (National Suicide Prevention Lifeline)
 * - Crisis Text Line (text HOME to 741741)
 * - 911 (Emergency Services)
 * - Campus/institutional crisis line (for education enterprise users)
 * 
 * Implementation per MHDF Enterprise Education Program Spec:
 * - Persistent in navigation across all dashboards
 * - Plain, accessible, non-alarming design
 * - Modal displays resources with confirmation message
 * - Configurable for institutional deployments (Phase 1)
 */

const SOSButton = ({ 
  campusCrisisLine = null, 
  institutionName = null,
  variant = 'navigation' // 'navigation' | 'dashboard' | 'floating'
}) => {
  const [showModal, setShowModal] = useState(false);

  const handleSOSClick = () => {
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
  };

  // Variant-specific button styling
  const buttonVariants = {
    navigation: 'flex items-center gap-2 rounded-full border-2 border-error-500 bg-white px-4 py-2 text-sm font-semibold text-error-600 shadow-md transition-all hover:bg-error-50 hover:border-error-600 focus:outline-none focus:ring-2 focus:ring-error-500 focus:ring-offset-2',
    dashboard: 'flex items-center justify-center gap-2 rounded-lg border-2 border-error-500 bg-white px-6 py-3 text-base font-semibold text-error-600 shadow-md transition-all hover:bg-error-50 hover:border-error-600 focus:outline-none focus:ring-2 focus:ring-error-500 focus:ring-offset-2',
    floating: 'fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full border-2 border-error-500 bg-error-500 px-6 py-4 text-base font-bold text-white shadow-2xl transition-all hover:bg-error-600 hover:border-error-600 hover:shadow-3xl focus:outline-none focus:ring-2 focus:ring-error-400 focus:ring-offset-2'
  };

  return (
    <>
      {/* SOS Button */}
      <button
        onClick={handleSOSClick}
        className={buttonVariants[variant]}
        aria-label="Emergency crisis resources"
        type="button"
      >
        <Shield className="h-5 w-5" />
        <span className="font-bold">Crisis Support</span>
      </button>

      {/* Crisis Resources Modal */}
      {showModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="crisis-modal-title"
        >
          <div className="relative w-full max-w-2xl rounded-lg bg-white shadow-2xl">
            {/* Header */}
            <div className="flex items-start justify-between border-b border-neutral-200 p-6">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-error-100">
                  <AlertCircle className="h-6 w-6 text-error-600" />
                </div>
                <div>
                  <h2 id="crisis-modal-title" className="text-2xl font-bold text-textPrimary">
                    Crisis Support Resources
                  </h2>
                  <p className="text-sm text-textSecondary">
                    You're not alone. Help is available 24/7.
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="rounded-lg p-2 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
                aria-label="Close modal"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Resources List */}
            <div className="space-y-4 p-6">
              
              {/* 988 - National Suicide Prevention Lifeline */}
              <div className="rounded-lg border-2 border-error-200 bg-error-50 p-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-error-500">
                    <Phone className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="mb-1 text-lg font-bold text-textPrimary">
                      988 - Suicide & Crisis Lifeline
                    </h3>
                    <p className="mb-2 text-sm text-textSecondary">
                      Call or text 988 for free, confidential support 24/7. 
                      Trained crisis counselors are ready to listen.
                    </p>
                    <a
                      href="tel:988"
                      className="inline-flex items-center gap-2 rounded-md bg-error-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-error-700"
                    >
                      <Phone className="h-4 w-4" />
                      Call 988
                    </a>
                  </div>
                </div>
              </div>

              {/* Crisis Text Line */}
              <div className="rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary">
                    <MessageSquare className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="mb-1 text-lg font-bold text-textPrimary">
                      Crisis Text Line
                    </h3>
                    <p className="mb-2 text-sm text-textSecondary">
                      Text <strong>HOME</strong> to <strong>741741</strong> to connect with a crisis counselor.
                    </p>
                    <a
                      href="sms:741741?body=HOME"
                      className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primaryDark"
                    >
                      <MessageSquare className="h-4 w-4" />
                      Text HOME to 741741
                    </a>
                  </div>
                </div>
              </div>

              {/* Campus/Institutional Crisis Line (if available) */}
              {campusCrisisLine && institutionName && (
                <div className="rounded-lg border border-support bg-supportLight p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-support">
                      <Phone className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="mb-1 text-lg font-bold text-textPrimary">
                        {institutionName} Crisis Line
                      </h3>
                      <p className="mb-2 text-sm text-textSecondary">
                        Connect with your campus crisis support team.
                      </p>
                      <a
                        href={`tel:${campusCrisisLine}`}
                        className="inline-flex items-center gap-2 rounded-md bg-support px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-supportDark"
                      >
                        <Phone className="h-4 w-4" />
                        Call {campusCrisisLine}
                      </a>
                    </div>
                  </div>
                </div>
              )}

              {/* 911 - Emergency Services */}
              <div className="rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-neutral-700">
                    <AlertCircle className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="mb-1 text-lg font-bold text-textPrimary">
                      911 - Emergency Services
                    </h3>
                    <p className="mb-2 text-sm text-textSecondary">
                      If you or someone else is in immediate danger, call 911.
                    </p>
                    <a
                      href="tel:911"
                      className="inline-flex items-center gap-2 rounded-md bg-neutral-700 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-neutral-800"
                    >
                      <Phone className="h-4 w-4" />
                      Call 911
                    </a>
                  </div>
                </div>
              </div>

              {/* Additional Resources */}
              <div className="rounded-lg bg-neutral-50 p-4">
                <h3 className="mb-2 text-sm font-semibold text-textPrimary">
                  Additional Resources
                </h3>
                <ul className="space-y-1 text-sm text-textSecondary">
                  <li>
                    <strong>SAMHSA National Helpline:</strong> 1-800-662-4357 (mental health support)
                  </li>
                  <li>
                    <strong>Veterans Crisis Line:</strong> Call 988, then press 1
                  </li>
                  <li>
                    <strong>Trevor Project (LGBTQ+):</strong> 1-866-488-7386 or text START to 678678
                  </li>
                </ul>
              </div>

              {/* Confirmation Message */}
              <div className="rounded-lg border-l-4 border-primary bg-primaryLight p-4">
                <p className="text-sm font-medium text-textPrimary">
                  <strong>You took an important step.</strong> Reaching out for help shows courage. 
                  These resources are confidential, free, and available 24/7.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-neutral-200 bg-neutral-50 px-6 py-4">
              <Button
                onClick={handleClose}
                variant="outline"
                className="w-full"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SOSButton;
